import type { DepreciationMethod, ScheduleEntry } from './types'

export type DepreciationInputs = {
  cost: number
  residualPercent: number
  usefulLifeYears: number
  durationMonths: number
  method: DepreciationMethod
  startDate: string // ISO YYYY-MM-DD
}

type Step = { amount: number; accumulated: number; balance: number }

/**
 * Build the full month-by-month depreciation schedule. Preserves the
 * legacy math exactly (see frontend/public/fixed_assets.html calculatePreview).
 */
export function buildDepreciationSchedule(inputs: DepreciationInputs): ScheduleEntry[] {
  const { cost, residualPercent, usefulLifeYears, durationMonths, method, startDate } = inputs
  if (!(cost > 0) || durationMonths <= 0 || usefulLifeYears <= 0) return []

  const residualValue = cost * (residualPercent / 100)
  const rows: Step[] = []
  let bookValueAtYearStart = cost

  const clampFinal = (currentDepr: number): number => {
    const accumDepr = rows.reduce((acc, c) => acc + c.amount, 0)
    if (cost - accumDepr - currentDepr < residualValue) {
      return Math.max(0, cost - accumDepr - residualValue)
    }
    return currentDepr
  }

  if (method === 'DDB' || method === 'DB') {
    const factor = method === 'DDB' ? 2 : 1.5
    const rate = (1 / usefulLifeYears) * factor
    for (let year = 1; year <= usefulLifeYears; year++) {
      const yearlyDepr = bookValueAtYearStart * rate
      const monthlyDepr = yearlyDepr / 12
      for (let month = 1; month <= 12; month++) {
        if (rows.length >= durationMonths) break
        const currentDepr = clampFinal(monthlyDepr)
        const accumDepr = rows.reduce((acc, c) => acc + c.amount, 0)
        rows.push({
          amount: currentDepr,
          accumulated: accumDepr + currentDepr,
          balance: cost - (accumDepr + currentDepr),
        })
      }
      if (rows.length >= durationMonths) break
      bookValueAtYearStart = cost - rows[rows.length - 1].accumulated
    }
  } else if (method === 'SLN') {
    const totalDepr = cost - residualValue
    const monthlyDepr = totalDepr / durationMonths
    for (let i = 0; i < durationMonths; i++) {
      const accumDepr = (i + 1) * monthlyDepr
      rows.push({ amount: monthlyDepr, accumulated: accumDepr, balance: cost - accumDepr })
    }
  } else {
    // SYD
    const depreciableBase = cost - residualValue
    const sumOfYears = (usefulLifeYears * (usefulLifeYears + 1)) / 2
    for (let year = 1; year <= usefulLifeYears; year++) {
      const remainingLife = usefulLifeYears - year + 1
      const yearlyDepr = depreciableBase * (remainingLife / sumOfYears)
      const monthlyDepr = yearlyDepr / 12
      for (let month = 1; month <= 12; month++) {
        if (rows.length >= durationMonths) break
        const currentDepr = clampFinal(monthlyDepr)
        const accumDepr = rows.reduce((acc, c) => acc + c.amount, 0)
        rows.push({
          amount: currentDepr,
          accumulated: accumDepr + currentDepr,
          balance: cost - (accumDepr + currentDepr),
        })
      }
      if (rows.length >= durationMonths) break
    }
  }

  const start = new Date(startDate + 'T00:00:00')
  return rows.map((s, i) => {
    const d = new Date(start)
    d.setMonth(start.getMonth() + i)
    return {
      period_number: i + 1,
      entry_date: d.toISOString().slice(0, 10),
      amount: roundCents(s.amount),
      secondary_amount: 0,
      balance: roundCents(s.balance),
      accumulated: roundCents(s.accumulated),
    }
  })
}

function roundCents(n: number): number {
  return Math.round(n * 100) / 100
}
